import { collectBoundaryEventIds, ganttMaxStackDepth } from "./chronosEventLayout";
import {
  MAX_STACK_TARGET,
  DEFAULT_ZOOM_MAX_SPAN_YEARS,
  DEFAULT_ZOOM_MIN_SPAN_MS,
} from "./chronosConstants";

const MS_PER_YEAR = 365.25 * 864e5;
const MAX_SPAN_MS = DEFAULT_ZOOM_MAX_SPAN_YEARS * MS_PER_YEAR;

/**
 * [min,max] time from non-era items (events + periods) for default zoom window.
 * Eras do not widen the initial domain.
 */
function rawPackableExtentMs(items) {
  let minT = Infinity;
  let maxT = -Infinity;
  for (const it of items) {
    if (it.type === "period" && it.isEra) continue;
    if (!it.startDate) continue;
    const s = it.startDate.getTime();
    const e = it.endDate ? it.endDate.getTime() : s;
    minT = Math.min(minT, s, e);
    maxT = Math.max(maxT, s, e);
  }
  if (!Number.isFinite(minT)) return null;
  return { minT, maxT };
}

function domainFromItems(items) {
  let minT = Infinity;
  let maxT = -Infinity;
  for (const it of items) {
    if (!it.startDate) continue;
    const s = it.startDate.getTime();
    const e = it.endDate ? it.endDate.getTime() : s;
    minT = Math.min(minT, s, e);
    maxT = Math.max(maxT, s, e);
  }
  if (!Number.isFinite(minT)) {
    const now = Date.now();
    return [new Date(now - 864e5 * 365), new Date(now + 864e5 * 365)];
  }
  if (minT === maxT) {
    return [new Date(minT - 864e5 * 30), new Date(maxT + 864e5 * 30)];
  }
  const pad = (maxT - minT) * 0.08;
  return [new Date(minT - pad), new Date(maxT + pad)];
}

/**
 * Padded span (ms) around packable item extent for default zoom.
 */
function idealSpanMsFromItems(items) {
  const ev = rawPackableExtentMs(items);
  if (ev) {
    const { minT, maxT } = ev;
    const raw = maxT - minT;
    if (raw <= 0) {
      return Math.max(864e5 * 60, DEFAULT_ZOOM_MIN_SPAN_MS);
    }
    const pad = raw * 0.08;
    return raw + 2 * pad;
  }
  const [d0, d1] = domainFromItems(items);
  return Math.max(d1.getTime() - d0.getTime(), DEFAULT_ZOOM_MIN_SPAN_MS);
}

function centeredDomain(centerMs, spanMs) {
  const half = spanMs / 2;
  return [new Date(centerMs - half), new Date(centerMs + half)];
}

/**
 * Compute [start, end] for initial view:
 * - Domain never wider than {@link DEFAULT_ZOOM_MAX_SPAN_YEARS} (500y).
 * - Extent from packable items (events + non-era periods); eras excluded from widening.
 * - Among spans ≤ cap, pick the **widest** that keeps Gantt row stacks ≤ MAX_STACK_TARGET.
 *
 * @param {Array} items — view model items
 * @param {Array} lanes — sorted lanes
 * @param {number} chartWidthPx
 * @param {'irl'|'erache'} [dateMode]
 */
export function computeDefaultViewDomain(items, lanes, chartWidthPx, dateMode) {
  const laneList = [...(lanes || [])].sort(
    (a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0)
  );
  const boundary = collectBoundaryEventIds(items);

  const ext = rawPackableExtentMs(items);
  let centerMs;
  if (ext) {
    centerMs = (ext.minT + ext.maxT) / 2;
  } else {
    const [d0, d1] = domainFromItems(items);
    centerMs = (d0.getTime() + d1.getTime()) / 2;
  }

  const idealSpan = idealSpanMsFromItems(items);
  const spanHi = Math.min(Math.max(idealSpan, DEFAULT_ZOOM_MIN_SPAN_MS), MAX_SPAN_MS);

  const w = Math.max(320, Math.round(chartWidthPx));
  const mode = dateMode === "irl" ? "irl" : "erache";

  const measureDepth = (viewStart, viewEnd) =>
    ganttMaxStackDepth(items, laneList, boundary, viewStart, viewEnd, w, mode);

  const depthForSpan = (spanMs) => {
    const [vs, ve] = centeredDomain(centerMs, spanMs);
    return measureDepth(vs, ve);
  };

  if (depthForSpan(spanHi) <= MAX_STACK_TARGET) {
    return centeredDomain(centerMs, spanHi);
  }

  if (depthForSpan(DEFAULT_ZOOM_MIN_SPAN_MS) > MAX_STACK_TARGET) {
    return centeredDomain(centerMs, DEFAULT_ZOOM_MIN_SPAN_MS);
  }

  let best = DEFAULT_ZOOM_MIN_SPAN_MS;
  let lo = DEFAULT_ZOOM_MIN_SPAN_MS;
  let hi = spanHi;
  while (lo <= hi) {
    const mid = Math.floor((lo + hi) / 2);
    if (depthForSpan(mid) <= MAX_STACK_TARGET) {
      best = mid;
      lo = mid + 1;
    } else {
      hi = mid - 1;
    }
  }
  return centeredDomain(centerMs, best);
}
